import BackArrow from '@/app/components/questionnaireV2/icons/back-arrow';
import {
  QuestionnaireCategoryItemsItemOneOf as Question,
  Questionnaire,
} from '@/api/generated-catch-backend/model';
import CloseIcon from '../../icons/close-icon';

function CategoryProgressIndicator({
  currentCategoryIndex,
  currentQuestionIndex,
  currentQuestionOnlyIndex,
  questions,
  questionToCategory,
  questionnaire,
}: {
  currentCategoryIndex: number;
  currentQuestionIndex: number;
  currentQuestionOnlyIndex: number;
  questions: Question[];
  questionToCategory: Map<number, number>;
  questionnaire: Questionnaire;
}) {
  const categoryProgress = questionnaire.categories.map(
    (category, categoryIndex) => {
      const questionsInCategory = questions.filter(
        (_, index) => questionToCategory.get(index) === categoryIndex,
      );

      const totalQuestionsInCategory = questionsInCategory.length;

      if (categoryIndex < currentCategoryIndex) {
        return 100;
      } else if (categoryIndex === currentCategoryIndex) {
        const answeredInCategory = questions
          .slice(0, currentQuestionIndex + 1)
          .filter(
            (_, index) => questionToCategory.get(index) === categoryIndex,
          ).length;

        return totalQuestionsInCategory > 0
          ? (answeredInCategory / totalQuestionsInCategory) * 100
          : 0;
      } else {
        return 0;
      }
    },
  );

  const categoryCount = questionnaire.categories.length;

  // For single category, calculate progress based on questions only
  const singleCategoryProgress = categoryCount === 1
    ? ((currentQuestionOnlyIndex + 1) / questions.length) * 100
    : categoryProgress[0];

  return (
    <div className="questionnaire-progress-container">
      {categoryCount > 1 ? (
        questionnaire.categories.map((category, index) => (
          <div key={index} className="questionnaire-progress-bar">
            <div
              className="questionnaire-progress-fill"
              style={{ width: `${categoryProgress[index]}%` }}
            />
          </div>
        ))
      ) : (
        <div className="single-questionnaire-progress-bar">
          <div
            className="questionnaire-progress-fill"
            style={{ width: `${singleCategoryProgress}%` }}
          />
        </div>
      )}
    </div>
  );
}

interface HeaderProps {
  currentCategoryIndex?: number;
  categoryName: string;
  subCategoryName?: string,
  totalCategories?: number;
  onBack: () => void;
  currentQuestionIndex?: number;
  currentQuestionOnlyIndex?: number;
  questions?: Question[];
  questionToCategory?: Map<number, number>;
  questionnaire?: Questionnaire;
  isBackDisabled?: boolean;
  isInterstitial?: boolean;
  showProgress?: boolean;
  showCloseIcon?: boolean;
  onClose?: () => void;
}

export function Header({
  currentCategoryIndex = 0,
  categoryName,
  subCategoryName,
  totalCategories = 1,
  onBack,
  currentQuestionIndex = 0,
  currentQuestionOnlyIndex = 0,
  questions = [],
  questionToCategory = new Map(),
  questionnaire,
  isBackDisabled = false,
  isInterstitial = false,
  showProgress = true,
  showCloseIcon = false,
  onClose,
}: HeaderProps) {
  if (isInterstitial) {
    return (
      <div className="absolute top-4 left-4 z-50">
        <button
          onClick={onBack}
          className={`inline-flex items-center justify-center p-2 rounded-full bg-black/20 backdrop-blur-sm ${isBackDisabled ? 'opacity-50' : 'hover:bg-black/30'} transition-colors`}
          disabled={isBackDisabled}
          style={{
            cursor: isBackDisabled ? 'not-allowed' : 'pointer',
            pointerEvents: isBackDisabled ? 'none' : 'auto',
          }}
        >
          <BackArrow />
        </button>
      </div>
    );
  }

  return (
    <div className="questionnaire-header-container">
      <video className="video-background" autoPlay loop muted playsInline>
        <source src="/videos/twilight.mp4" type="video/mp4" />
      </video>
      <div className="questionnaire-header-content">
        <button
          onClick={onBack}
          className={`questionnaire-back-button ${isBackDisabled ? 'questionnaire-back-button--disabled' : ''}`}
          disabled={isBackDisabled}
          style={{
            opacity: isBackDisabled ? 0.5 : 1,
            cursor: isBackDisabled ? 'not-allowed' : 'pointer',
            pointerEvents: isBackDisabled ? 'none' : 'auto',
          }}
        >
          <BackArrow />
        </button>

        <div className="questionnaire-title-container">
          <h1 className="text-2xl">{categoryName}</h1>
          {
            subCategoryName && <h3 className="text-lg">{subCategoryName}</h3>
          }
        </div>

        <div className="questionnaire-counter-container">
          {showCloseIcon ? (
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300 transition-colors p-2"
              aria-label="Close"
            >
              <CloseIcon/>
            </button>
          ) : (
            <span className="questionnaire-category-counter">
              {totalCategories > 1
                ? `${currentCategoryIndex + 1}/${totalCategories}`
                : `${currentQuestionOnlyIndex + 1}/${questions.length}`}
            </span>
          )}
        </div>
      </div>
      {showProgress && questionnaire && (
        <CategoryProgressIndicator
          currentCategoryIndex={currentCategoryIndex}
          currentQuestionIndex={currentQuestionIndex}
          currentQuestionOnlyIndex={currentQuestionOnlyIndex}
          questions={questions}
          questionToCategory={questionToCategory}
          questionnaire={questionnaire}
        />
      )}
    </div>
  );
}
